import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Linking, Pressable, View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import {
  CompactActionButton,
  ActionBanner,
  CardSurface,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  AlertTriangle,
  Phone,
  Syringe,
  Volume2,
  VolumeX,
} from "lucide-react-native";

import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  COUNTRIES,
  getCountryName,
} from "@src/packages/location/countryMetadata";
import { telHref } from "../emergencyNumbers";
import {
  useCoachEscalationContacts,
  type CoachEscalationContacts,
} from "../hooks/useCoachEscalationContacts";
import type { Framing } from "../pose/zones";
import {
  canAdvance,
  canGoBack,
  currentStep,
  reduce,
  startSession,
  type SessionState,
} from "@src/packages/injection-protocol/session";
import {
  renderProtocolCopy,
  type ProtocolCopyContext,
} from "@src/packages/injection-protocol/brand";
import type {
  InjectionProtocol,
  ProtocolSite,
  ProtocolStep,
  ProtocolBox,
} from "@src/packages/injection-protocol/types";
import { doseDecision } from "@src/packages/injection-protocol/dose";
import type { DoseQuestion as DoseQuestionState } from "../doseQuestion";
import { actionCarriesConfirmation, advanceLabelKey } from "../stepActions";
import { splitStepCopy } from "../stepCopy";
import { figureForStep, stepName } from "../overlay/figures";
import { StepOverlay } from "./overlay/StepOverlay";
import { speakableStepText, speechLanguage, spokenStepText } from "../speech";
import { useStepSpeech } from "../hooks/useStepSpeech";
import { InjectionCoachCameraStage } from "./InjectionCoachCameraStage";
import { PREVIEW_DETECTS_POSE } from "./CameraPreview";
import { cameraRoleFor } from "../cameraRole";
import type { ThighSide } from "../thighSide";
import { administrationMethodLabelKey } from "../deviceChoices";
import { ROUTE_CHOICES } from "../routeChoices";
import { useCoachRecords } from "../records/useCoachRecords";
import { semanticColors, colors, withAlpha } from "@louise/theme/tokens";

const ns = "care.injection_coach";

/**
 * The guided walk itself, with no opinion about where it is being shown.
 *
 * Extracted because the walk had exactly one host — the trigger-shot step — and
 * every other surface that wanted to offer coaching had to invent a destination
 * to send her to. That was the wrong shape: it made the medication factsheet's
 * entry button depend on a screen that could open a coach, rather than on a
 * protocol that could be walked.
 *
 * So the walk owns the session and nothing else. The caller decides where it
 * appears and what a completed walk means — the trigger step records the dose
 * against her schedule, a factsheet records nothing, and neither of those
 * choices belongs to the steps.
 */

/**
 * Which card each step sits on. On a page it is the glass card; in the coach's
 * own solid sheet glass has no border to see, so the sheet asks for the muted
 * card and each step still reads as a card.
 */
const CoachCardSurfaceContext = createContext<"glass" | "muted">("glass");
export const CoachCardSurfaceProvider = CoachCardSurfaceContext.Provider;

export function Card({ children }: { children: React.ReactNode }) {
  const surface = useContext(CoachCardSurfaceContext);
  return (
    <CardSurface
      variant={surface}
      style={{ padding: 22, gap: 18, marginTop: 16 }}
    >
      {children}
    </CardSurface>
  );
}

export function InjectionCoachWalk({
  protocol,
  onVialAlreadyMixed,
  onWalkStarted,
  brandName,
  prescribedDose,
  patientMedicationId,
  withCamera = false,
  onCallClinic,
  onFinished,
  onWalkCompleted,
  completionHeadline,
  completionExtra,
  finishLabelKey = "finish_and_close",
}: {
  protocol: InjectionProtocol;
  /**
   * Her way out of the mixing steps, on a walk that assumed the first evening.
   *
   * Supplied by the host only when a sibling walk exists for the already-mixed
   * vial, and offered only on the steps that prepare it — past those something
   * has been drawn and there is nothing left to skip. Calling it changes which
   * procedure the host hands down, which is what restarts the session below.
   */
  onVialAlreadyMixed?: () => void;
  /**
   * She pressed start, and the procedure is now the one she is standing in.
   *
   * The host stops applying a late-arriving record from here: it may decide
   * which walk she is offered and may never swap the walk under her hands.
   * Fires on every start, including a restart onto the sibling procedure and
   * "Start over", because all three mean the same thing to the caller.
   */
  onWalkStarted?: () => void;
  /** Taken from her prescription, never from her locale. */
  brandName: string;
  /** Her recorded dose, so no step names a number that is not hers. */
  prescribedDose?: string | null;
  /**
   * The scheduled dose this walk is for, when there is one.
   *
   * Sent with the session record so it says which dose was coached. Nothing on
   * screen depends on it, and a reference walk simply has none.
   */
  patientMedicationId?: string | null;
  /**
   * Whether she asked for the camera in the disclosure, and the OS agreed.
   *
   * Defaults to off, so a host that has not taken her through a disclosure
   * cannot start a camera — the inline panel on the cycle screen is one, and it
   * must stay a no-camera surface until it asks.
   */
  withCamera?: boolean;
  onCallClinic: () => void;
  /**
   * How she left the walk.
   *
   * `finished` and `aborted` mean she is done with it — a modal host closes,
   * an inline host collapses. `restarted` means she is still here.
   */
  onFinished?: (status: "finished" | "aborted" | "restarted") => void;
  /**
   * Fires once, on the edge into `complete`.
   *
   * Once, because effects re-run under StrictMode and a dose logged twice is a
   * dose she did not take. The walk does not care what the caller does with it —
   * the trigger step records against her schedule, a factsheet does nothing.
   */
  onWalkCompleted?: () => void;
  /**
   * What the completion screen says. The trigger step overrides it to follow the
   * dose record; a surface that records nothing must not claim anything was.
   */
  completionHeadline?: (status: SessionState["status"]) => string;
  /** Extra action on the completion screen, such as a failed-save retry. */
  completionExtra?: (status: SessionState["status"]) => React.ReactNode;
  /**
   * What the finish action says, when finishing leads somewhere.
   *
   * Conditional because it is a promise about what the tap does. On a surface
   * that hands her to the dose logger it should say so; on one that just closes
   * — a factsheet opened as reference, with no dose behind it — announcing a
   * confirmation that never comes would be worse than the plain word.
   */
  finishLabelKey?: string;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  /**
   * Resolved here rather than inside the paused card, so the queries start when
   * the coach opens and have the whole walk to land.
   *
   * Asking at the moment of the pause would be asking at the worst moment: she
   * would watch a fallback button turn into a phone number while being told to
   * get emergency help.
   */
  const contacts = useCoachEscalationContacts();
  /**
   * The camera, as it stands right now rather than as she first answered.
   *
   * Separate from `withCamera` because the disclosure promises she can turn it
   * off at any moment, and a promise that reads from the original answer would
   * turn it back on at the next step. Once off, it stays off for this walk —
   * turning it back on would be a new decision, and this surface is not where
   * that decision gets made.
   */
  const [cameraOn, setCameraOn] = useState(withCamera);
  /**
   * Whether she has finished pointing the phone at herself.
   *
   * The camera runs for one step — the injection — and before that step guides
   * anything, she has to aim it. She needs both hands, so the phone is propped
   * rather than held, and a diagram drawn over a frame she has not finished
   * aiming would be instructing her before she is ready.
   */
  const [framed, setFramed] = useState(false);
  const [framing, setFraming] = useState<Framing>("starting");
  /**
   * Whether the camera can put an outline on her body at all.
   *
   * The card hides its own diagram while the camera is on, on the grounds that
   * the camera is drawing a better one. That was assumed rather than known: a
   * phone has no detector, and on the web the model can fail to load — and in
   * both cases the card hid its diagram in favour of nothing, leaving the one
   * irreversible step as the only step in the walk with no illustration at all.
   *
   * Capability rather than what is on screen this instant. An outline comes and
   * goes as she moves in and out of frame, and a diagram appearing and
   * disappearing in the card underneath it would be worse than either.
   */
  const [cameraCanOutline, setCameraCanOutline] =
    useState(PREVIEW_DETECTS_POSE);
  /**
   * The site she picked, on a procedure that offers more than one.
   *
   * Null until she answers, and asked once per walk rather than once per day:
   * the choice is about the body she has today — a bruised abdomen, a tender
   * one late in stimulation — so remembering yesterday's answer would be the
   * app deciding for her on the wrong day's information.
   */
  const [site, setSite] = useState<ProtocolSite["id"] | null>(null);
  /**
   * Which leg, once she has said the thigh.
   *
   * A second question rather than four buttons on the first one: "abdomen,
   * left thigh, right thigh" puts a choice about sides in front of a patient
   * who may be about to answer "abdomen", and the sites come from the approved
   * content — the legs do not, and a row that mixes the two would read as
   * though the label named a leg.
   *
   * Asked once per walk, like the site, and for the same reason: yesterday's
   * leg is the one with yesterday's bruise on it.
   */
  const [leg, setLeg] = useState<ThighSide | null>(null);

  /**
   * A step, rewritten for the site she chose.
   *
   * The two sites are not one sentence with a word swapped — an abdomen has a
   * distance from the navel and a thigh does not — so the protocol carries a
   * whole sentence for each and this swaps in the one she picked. Applied in a
   * single place so the card and the voice cannot say different things, which
   * is the rule `speech.ts` exists to keep.
   */
  const atSite = useCallback(
    (candidate: ProtocolStep | null): ProtocolStep | null => {
      if (!candidate || !site) return candidate;
      if (stepName(candidate.id) !== "clean-site") return candidate;
      const chosen = (protocol.sites ?? []).find((one) => one.id === site);
      return chosen ? { ...candidate, copy: chosen.copy } : candidate;
    },
    [protocol.sites, site],
  );
  /**
   * Reading the steps aloud. Off until she asks.
   *
   * Speaking is the half of this that works with a needle in hand, so it is not
   * buried — but starting it unprompted would say her medication and her dose
   * out loud in whatever room she is standing in, and nothing she agreed to
   * mentioned sound. The default is silence; the control is in plain sight.
   */
  const [voiceOn, setVoiceOn] = useState(false);
  const [session, setSession] = useState<SessionState | null>(null);
  /**
   * She tapped "Something's wrong" and is choosing what it is.
   *
   * View state, not session state: nothing has been raised yet, so the reducer
   * must not be told anything. Reaching for help is not itself a warning.
   */
  const [choosing, setChoosing] = useState(false);

  // Nothing but the reducer here. A side effect inside the updater would run
  // again every time React replays it — under StrictMode, or when an update is
  // rebased — and a dose logged twice is a dose she did not take.
  const dispatch = useCallback((event: Parameters<typeof reduce>[1]) => {
    setSession((previous) => (previous ? reduce(previous, event) : previous));
  }, []);

  /**
   * The one way a walk ends, so nothing of it outlives it.
   *
   * `framed` used to be cleared only when she turned the camera off, and the
   * host deliberately keeps this component mounted through "Start over" — so a
   * second walk began already framed. The camera then opened straight into
   * guiding at the injection step: no aiming screen, and none of the sentences
   * that tell her to prop the phone because she is about to need both hands.
   * She was holding it one-handed at the step that screen exists to prevent.
   *
   * `cameraOn` is deliberately *not* reset. Turning the camera off is her
   * decision, and starting a new walk is not where it gets reversed.
   */
  /**
   * The record of this walk, written as it happens.
   *
   * Best effort throughout — see `useCoachRecords`. Nothing here is awaited and
   * no failure reaches the screen: her rule is that the coach keeps working
   * when the network does not, and a woman holding a needle is the last person
   * who should be told a request failed.
   */
  const { startWalk, recordEvent, finishWalk, abandonWalk, forgetWalk } =
    useCoachRecords();

  const endWalk = useCallback(() => {
    setSession(null);
    setFramed(false);
    // Asked again next time: the answer is about the body she has today,
    // and a bruised abdomen is not a fact that survives to tomorrow.
    setSite(null);
    setLeg(null);
    // And restored with it. A thigh tells the stage it cannot outline
    // anything, which is true of a thigh and not of the walk — left latched, a
    // thigh walk followed by an abdomen one showed the outline on her body and
    // the diagram in the card at the same time, which is the one thing this
    // flag exists to prevent.
    setCameraCanOutline(PREVIEW_DETECTS_POSE);
    setFraming("starting");
    // A new walk collects no events from the one she left.
    forgetWalk();
  }, [forgetWalk]);

  /**
   * Only a dose the label can place reaches a step.
   *
   * The host asks her before the walk when her record disagrees with the
   * label, and hands down either her answer or nothing. This is the same rule
   * held here as well, so a surface that opens the walk directly — the
   * trigger-step panel does — cannot have it repeat "150 mg" for a microgram
   * drug. A mismatch counts as nothing recorded, everywhere.
   */
  const sayable = doseDecision(protocol, prescribedDose);
  const copy: ProtocolCopyContext = {
    locale: locale === "fr" ? "fr" : "en",
    brandName,
    prescribedDose: sayable.kind === "matches" ? sayable.dose : null,
  };

  /**
   * Where she will inject, asked once, just before she cleans anything.
   *
   * Only on a procedure whose approved content names more than one site, and
   * only at the step where the answer starts to matter — asking at the top of
   * the walk would put a question about her body in front of a patient who has
   * not yet washed her hands.
   *
   * It is not a gate she can fail: both answers are approved, and the camera
   * afterwards does whatever it honestly can at the site she named.
   */
  const currentSiteStep = session ? currentStep(session) : null;
  const askingSite =
    currentSiteStep !== null &&
    stepName(currentSiteStep.id) === "clean-site" &&
    (protocol.sites?.length ?? 0) > 1 &&
    site === null;
  /**
   * A procedure with exactly one site has already answered the question.
   *
   * Nothing authored today does that, and if something did, the guide read
   * `site === null` as an abdomen: the torso silhouette, and the abdominal area
   * outlined over her leg. Taking the single site as her answer costs one line
   * and removes a class of authoring mistake rather than a bug.
   */
  const onlySite = protocol.sites?.length === 1 ? protocol.sites[0] : null;
  useEffect(() => {
    if (onlySite && site === null) setSite(onlySite.id);
  }, [onlySite, site]);
  /**
   * And which leg, once she has said the thigh and before anything is aimed.
   *
   * Same step, immediately after the site: the answer is what the silhouette
   * and the band are drawn from, so asking it later would mean showing her a
   * guide for a leg she has not chosen.
   */
  const askingLeg =
    currentSiteStep !== null &&
    stepName(currentSiteStep.id) === "clean-site" &&
    site === "thigh" &&
    leg === null;

  /**
   * Whether the card on screen is the aiming screen rather than the step.
   *
   * Computed here, before the speech, because the voice has to know: the
   * injection step exists in the session from the moment she reaches it, but
   * while she is still pointing the phone the card in front of her is the
   * framing screen. The voice read "Inject — pinch the skin…" and its cautions
   * over a card that said "Set your phone so it can see you", which is the
   * difference between what she hears and what she reads that `speech.ts`
   * forbids.
   */
  /**
   * Whether her camera is a mirror on this procedure rather than a guide.
   *
   * Read from the route, once, because four things downstream depend on it —
   * which steps show the picture, whether there is an aiming screen, what the
   * voice says while one is up, and whether the pose model starts at all. See
   * `cameraRole.ts`.
   */
  const mirroring = cameraRoleFor(protocol, site) === "mirror";

  const speakingAim =
    cameraOn &&
    !framed &&
    !mirroring &&
    session !== null &&
    currentStep(session)?.kind === "irreversible";

  /**
   * What the voice says while she is aiming, which is not the step.
   *
   * This screen is where a voice earns the most: the phone is propped a step or
   * two away, she is looking at her own abdomen, and the words telling her
   * whether the camera can see her are on a surface she has just walked away
   * from. It read the injection step here instead — the step exists in the
   * session from the moment she reaches it — so she heard "Inject — pinch the
   * skin…" over a card that said "Set your phone so it can see you".
   *
   * The instruction once, then only the states worth hearing. Repeating the
   * heading every time the camera found or lost her would bury the one word
   * that changed — and most of the states are not worth saying at all: see
   * `SPOKEN_FRAMING`.
   *
   * **Held for a second before it is spoken.** Framing is computed from raw
   * readings twelve times a second and flips the moment a landmark leaves the
   * frame, so announcing every transition would chatter at her while she is
   * still putting the phone down. A state that does not survive a second was
   * not worth saying.
   */
  const [aimUtterance, setAimUtterance] = useState<string | null>(null);
  const aimIntroSpoken = useRef(false);
  useEffect(() => {
    if (!speakingAim || !voiceOn) {
      aimIntroSpoken.current = false;
      setAimUtterance(null);
      return;
    }

    if (!aimIntroSpoken.current) {
      aimIntroSpoken.current = true;
      setAimUtterance(
        [
          t(
            site === "thigh"
              ? `${ns}.sit_down_and_set_your_phone_so_it_sees_your_leg`
              : `${ns}.set_your_phone_so_it_can_see_you`,
          ),
          t(
            site === "thigh"
              ? `${ns}.your_hip_and_your_knee_both_in_view`
              : `${ns}.stand_back_until_your_shoulders_and_hips_are_in_view`,
          ),
        ].join(". "),
      );
      return;
    }

    // Anything else leaves the last thing said standing rather than replacing
    // it with silence — the voice simply does not comment.
    if (!SPOKEN_FRAMING.has(framing)) return;

    const status = t(`${ns}.${FRAMING_MESSAGE[framing]}`);
    const settle = setTimeout(() => setAimUtterance(status), 1000);
    return () => clearTimeout(settle);
  }, [speakingAim, voiceOn, framing, site, t]);

  // Silent unless she is actually working through a step: not while paused on a
  // warning, not while choosing what is wrong, not on the completion card, and
  // not while she is still aiming the phone — where it reads the aiming screen
  // instead. A voice still reading the procedure over an instruction to call
  // her clinic is the failure this guards against.
  const speakingStep =
    voiceOn &&
    !choosing &&
    !speakingAim &&
    !askingSite &&
    !askingLeg &&
    session?.status === "in-progress"
      ? atSite(currentStep(session))
      : null;
  useStepSpeech({
    enabled: voiceOn,
    // The instruction, then whatever caution sits on it — assembled in
    // `speech.ts`, which is where the rules about what may and may not be said
    // out loud are written down and tested.
    text: speakingStep
      ? spokenStepText(speakingStep, copy)
      : // The aiming screen, when that is what is in front of her. Through the
        // same normaliser as the steps: it is written to be read, and a dash
        // between two clauses is read aloud as the word "dash".
        (aimUtterance && speakableStepText(aimUtterance, copy.locale)) || null,
    language: speechLanguage(copy.locale),
    locale: copy.locale,
  });

  /**
   * Every step she is shown, once.
   *
   * Keyed on the step id rather than on a render, because the walk re-renders
   * several times a second while the camera is on and a record per frame would
   * describe the renderer rather than the procedure.
   */
  const presentedStepId = session ? currentStep(session)?.id : undefined;
  useEffect(() => {
    if (!presentedStepId) return;
    void recordEvent("step_presented", presentedStepId);
  }, [presentedStepId, recordEvent]);

  /**
   * Leaving without finishing, by any of the ways she can leave.
   *
   * The completion card and the abort button both close their own record. The
   * X, the back button, the host closing underneath, a navigation away — none
   * of them did, and they are the likeliest ways out of a walk she has thought
   * better of. An unmount with a session still open is an abandonment, and
   * saying so is the difference between a record that ends and one that stays
   * open forever with no outcome.
   *
   * On unmount only, deliberately: `finishWalk` is a no-op once the session is
   * already closed, so a normal completion cannot be overwritten by this.
   */
  const leaveWalk = useRef<() => void>(() => {});
  leaveWalk.current = () => {
    // Only if a walk is actually open — see `abandonWalk`. This fires on every
    // unmount, including the one between the two halves of a combined trigger,
    // where the session has already closed properly.
    void abandonWalk();
  };
  useEffect(() => () => leaveWalk.current(), []);

  const isComplete = session?.status === "complete";
  const hasAnnouncedRef = useRef(false);
  useEffect(() => {
    if (!isComplete) {
      hasAnnouncedRef.current = false;
      return;
    }
    if (hasAnnouncedRef.current) return;
    hasAnnouncedRef.current = true;
    // The record of the walk closes here, and says only what it knows: she
    // reached the last step. Whether the dose was taken is her answer on the
    // completion card, which the host records against her schedule — #2267
    // forbids manufacturing that from anything less.
    void recordEvent("completed");
    void finishWalk("coach_session_completed");
    onWalkCompleted?.();
  }, [isComplete, onWalkCompleted, recordEvent, finishWalk]);

  /**
   * She said the vial was already mixed, and the host handed down the sibling.
   *
   * Restarted rather than continued. A session holds the protocol it began on,
   * the two walks do not share a step index, and the one she is moving to
   * carries a check this one has no reason to: whether the vial passed its 28
   * days. Skipping ahead inside this walk would have dropped that check on the
   * one patient who needs it — the walk exists because her vial is old.
   *
   * Narrow on purpose. It fires for this one transition and no other, so it
   * can never be what moves a combined trigger between its two halves.
   *
   * The record follows her: the first walk closes as abandoned with no
   * outcome, which is what happened to it, and the new one opens in its own
   * row. Chained rather than fired together, because `finishWalk` releases the
   * session id synchronously and a `startWalk` racing it would have the
   * abandonment close the walk she had just started.
   */
  const walkedProtocol = session?.protocol ?? null;
  useEffect(() => {
    if (!walkedProtocol) return;
    if (walkedProtocol.presentation !== "multidose-first") return;
    if (protocol.presentation !== "multidose-later") return;
    setSession(startSession(protocol));
    onWalkStarted?.();
    void abandonWalk().then(() =>
      startWalk({
        protocolSlug: protocol.medicationSlug,
        protocolVersion: protocol.version,
        brandName,
        deviceForm: protocol.form,
        cameraEnabled: cameraOn,
        ...(patientMedicationId ? { patientMedicationId } : {}),
      }),
    );
  }, [
    walkedProtocol,
    protocol,
    abandonWalk,
    startWalk,
    onWalkStarted,
    brandName,
    cameraOn,
    patientMedicationId,
  ]);

  if (!session) {
    return (
      <NotStarted
        t={t}
        protocol={protocol}
        onStart={() => {
          setSession(startSession(protocol));
          onWalkStarted?.();
          void startWalk({
            protocolSlug: protocol.medicationSlug,
            protocolVersion: protocol.version,
            brandName,
            deviceForm: protocol.form,
            cameraEnabled: cameraOn,
            ...(patientMedicationId ? { patientMedicationId } : {}),
          });
        }}
      />
    );
  }

  if (session.status === "complete" || session.status === "aborted") {
    const headline =
      completionHeadline?.(session.status) ??
      (session.status === "aborted"
        ? `${ns}.stopped_for_now`
        : `${ns}.injection_walk_complete`);

    return (
      <Card>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
        >
          {t(headline)}
        </Text>
        {completionExtra?.(session.status)}
        {/* Two ways out, because reaching the end is not the same as wanting
            to leave — she may want to read the steps again straight away, and
            "start over" as the only action reads like the walk is not finished
            until she does. */}
        <CompactActionButton
          variant="primary"
          label={t(`${ns}.${finishLabelKey}`)}
          onPress={() => {
            endWalk();
            onFinished?.("finished");
          }}
        />
        <CompactActionButton
          variant="secondary"
          label={t(`${ns}.start_over`)}
          onPress={() => {
            endWalk();
            onFinished?.("restarted");
          }}
        />
      </Card>
    );
  }

  if (choosing) {
    return (
      <WarningChooser
        t={t}
        session={session}
        copy={copy}
        contacts={contacts}
        onCallClinic={() => {
          void recordEvent("escalated");
          onCallClinic();
        }}
        onCallRecorded={() => void recordEvent("escalated")}
        onBack={() => setChoosing(false)}
        onAbort={() => {
          void recordEvent("abandoned");
          void finishWalk("outcome_unknown");
          endWalk();
          onFinished?.("aborted");
        }}
      />
    );
  }

  const step = atSite(currentStep(session));

  /**
   * The camera runs where her hands are working on her body, and nowhere else.
   *
   * It used to run for the whole walk. Seeing herself only changes what her
   * hands do at the steps where her hands are doing something on her body — a
   * camera on while she washes her hands or reads a clock is a camera on for no
   * reason, and on a surface built around not keeping anything, "for no reason"
   * is the wrong default.
   *
   * That is the injection, plus one step on a mirror: cleaning the site. The
   * two are not symmetric, and the asymmetry is the point. A guide draws on her
   * abdomen, which she can see perfectly well without a phone — swabbing it
   * needs no picture. A mirror exists because the site is behind her, and the
   * swab has to land on the same quadrant the needle will: asking her to find
   * it blind, then handing her a mirror one step later to find it again, is the
   * camera arriving after the part it was for.
   */
  const stepShowsCamera =
    step !== null &&
    (step.kind === "irreversible" ||
      (mirroring && stepName(step.id) === "clean-site"));
  const showCamera = cameraOn && stepShowsCamera && !askingSite && !askingLeg;
  /**
   * Aiming exists to get an outline onto her body, so a mirror skips it.
   *
   * There is nothing to frame when nothing is being placed: no silhouette to
   * fill, no detector to satisfy, and no "we can see you" to wait for. Holding
   * her on a framing screen before an intramuscular injection would be asking
   * her to satisfy a check that does not exist.
   */
  const aiming = showCamera && !framed && !mirroring;

  /**
   * Kept in a variable and given a key, because it moves.
   *
   * While she is aiming, the picture is the subject and goes above the words.
   * Once the injection begins the instruction is the subject and the picture
   * goes under it — she reads what to do, then looks at herself doing it.
   *
   * Moving a component between two positions among its siblings normally
   * unmounts and remounts it, which here would drop the camera stream and
   * re-check the permission at the exact moment she is about to inject. A key
   * is what lets React carry it across the move instead.
   */
  const stage =
    showCamera && step ? (
      <InjectionCoachCameraStage
        key="stage"
        step={step}
        protocol={protocol}
        site={site}
        leg={leg}
        phase={framed || mirroring ? "guiding" : "framing"}
        onTurnOff={() => {
          setCameraOn(false);
          setFramed(false);
        }}
        onFraming={setFraming}
        onCannotOutline={() => setCameraCanOutline(false)}
      />
    ) : null;

  const card =
    askingSite && step ? (
      <SiteQuestion
        key="card"
        t={t}
        copy={copy}
        sites={protocol.sites ?? []}
        onChoose={setSite}
        onSomethingWrong={() => setChoosing(true)}
      />
    ) : askingLeg && step ? (
      <LegQuestion
        key="card"
        t={t}
        onChoose={setLeg}
        onSomethingWrong={() => setChoosing(true)}
      />
    ) : aiming ? (
      <FramingStep
        key="card"
        t={t}
        framing={framing}
        shape={site === "thigh" ? "thigh" : "torso"}
        voiceOn={voiceOn}
        onToggleVoice={() => setVoiceOn((on) => !on)}
        onReady={() => {
          void recordEvent("patient_ready");
          setFramed(true);
        }}
      />
    ) : (
      <InProgress
        key="card"
        t={t}
        session={session}
        step={step}
        copy={copy}
        onAdvance={() => dispatch({ type: "advance" })}
        // Two events, one tap. The reducer still sees the confirmation land
        // before the advance — its contract is about events, not about buttons.
        onConfirmAndAdvance={() => {
          dispatch({ type: "confirm" });
          dispatch({ type: "advance" });
        }}
        // Stepping back off the cleaning step forgets the site and the leg, so
        // the questions are asked again on the way forward. They are asked once
        // and have no answer of their own to change, which made a mis-tap on
        // "Upper thigh" or on the wrong leg undoable except by stopping the
        // walk — and for the leg the mistake is invisible until the guide
        // appears on the wrong side. This is the way back that already existed;
        // it just was not clearing anything.
        onBack={() => {
          if (step && stepName(step.id) === "clean-site") {
            setSite(null);
            setLeg(null);
          }
          dispatch({ type: "back" });
        }}
        onSomethingWrong={() => setChoosing(true)}
        voiceOn={voiceOn}
        onToggleVoice={() => setVoiceOn((current) => !current)}
        // The diagram belongs on every step, with or without a camera. Where the
        // camera is showing it over her body it must not also appear in the card,
        // or she is looking at the same drawing twice.
        // The card keeps its own diagram unless the camera is drawing a better
        // one on her body. "A camera is on" was standing in for that and is not
        // the same thing: a phone has no detector and the web model can fail to
        // load, and in both cases this hid the diagram in favour of nothing — on
        // the one step of the walk where the needle is.
        // Only while the step is still preparing the vial, and only where the
        // host has a sibling walk to send her to. Past the mixing, something
        // has been drawn and there is nothing left to skip.
        alreadyPrepared={
          onVialAlreadyMixed && step?.preparesTheVial
            ? onVialAlreadyMixed
            : null
        }
        showFigure={!(showCamera && cameraCanOutline)}
        // Only where this step would show a picture, and only when she agreed to
        // a camera in the first place: this offers the thing she already
        // consented to, never a new one.
        // Only with a picture on screen. Off, it is advice about nothing.
        placementHint={showCamera && mirroring}
        site={site}
        cameraOffer={
          withCamera && !cameraOn && stepShowsCamera
            ? {
                labelKey: mirroring
                  ? "use_the_camera_as_a_mirror"
                  : "turn_the_camera_back_on",
                onTurnOn: () => setCameraOn(true),
              }
            : null
        }
      />
    );

  // Not on the warning chooser, not while paused, not on the completion card:
  // those are the moments she is reading rather than doing, and a live picture
  // of herself competes with the thing she needs to read.
  return <>{aiming ? [stage, card] : [card, stage]}</>;
}

/**
 * Aiming the phone, before the injection begins.
 *
 * She needs both hands for the next step, so the phone has to be propped — and
 * nothing in the walk said so until now.
 *
 * It asks for her shoulders and hips to be in shot, which is not where she
 * would naturally point it. It has to: the detector reads a torso, so an
 * instruction to aim at her stomach guaranteed a close-up in which nothing
 * could be found, and she spent the step fighting to centre herself. Asking for
 * the framing the detector can actually use is the difference between a step
 * that works and one that argues with her.
 *
 * **Continuing is never blocked.** The three states below are advice, not a
 * gate: the detector is wrong sometimes, some platforms have none at all, and a
 * patient who cannot get past a framing screen is a patient who cannot take her
 * medication. So the action is always live, and reads as encouragement rather
 * than permission once the framing is good.
 */
/**
 * What each framing state says to her.
 *
 * Kept as a table so the states and the sentences cannot drift apart: adding a
 * state without a message stops compiling. `unavailable` is deliberately about
 * the phone rather than about her — it is not a failure she can act on, and
 * telling her otherwise is what the old three-state version did on every
 * device that has no detector.
 */
// Read by name out of this file by `copy.test.ts`, which cannot import a
// component module: a key chosen at runtime goes missing in front of a patient
// exactly like a static one, and a hand-copied list is how it stops being
// covered. Renaming this const fails that guard rather than emptying it.
/**
 * Which framing states the voice says out loud.
 *
 * Not the same set as the ones on screen, and deliberately smaller. She turns
 * the voice on because she cannot watch the screen — so what it says has to be
 * worth hearing while she is propping a phone against a shelf with a pen in the
 * other hand.
 *
 * **`searching` is not spoken.** "We can't see you yet" is fine to glance at and
 * unpleasant to be told, repeatedly, by a voice, while you are trying to stand
 * in the right place — and it is the state the detector falls into every time a
 * shoulder leaves the frame. On screen it explains why nothing is outlined;
 * said aloud it is a machine telling her she is doing it wrong.
 *
 * `starting` is not spoken either — announcing a wait is noise. What is left is
 * what she can act on: she is framed, she should come closer, or nothing is
 * coming on this phone and she need not wait for it.
 */
const SPOKEN_FRAMING: ReadonlySet<Framing> = new Set<Framing>([
  "ready",
  "too-far",
  "unavailable",
]);

const FRAMING_MESSAGE: Readonly<Record<Framing, string>> = {
  unavailable: "no_guide_here_use_the_picture_as_a_mirror",
  starting: "starting_the_camera_guide",
  searching: "we_cannot_see_you_yet",
  "too-far": "come_a_little_closer",
  ready: "we_can_see_you",
};

/**
 * The control that turns the reading voice on and off.
 *
 * One component because it belongs on two cards. It was on the step card only,
 * and the aiming screen is where a voice is worth the most: the phone is
 * propped a step away, she is looking at her own abdomen rather than at the
 * screen, and the words telling her whether the camera can see her are on a
 * surface she has just walked away from.
 *
 * It does not position itself. The step card pins it in the corner beside the
 * step counter, where there is room; the aiming card gives it its own row,
 * because pinning it over a full-width heading would either cover the heading
 * or squeeze it into four lines in French. Right-aligned on both, and on
 * neither does it sit beside the button that advances past an injection.
 */
function VoiceToggle({
  t,
  voiceOn,
  onToggleVoice,
}: {
  t: ReturnType<typeof useTranslations>;
  voiceOn: boolean;
  onToggleVoice: () => void;
}) {
  return (
    <Pressable
      testID="injection-coach-toggle-voice"
      accessibilityRole="switch"
      accessibilityState={{ checked: voiceOn }}
      accessibilityLabel={t(
        voiceOn ? `${ns}.stop_reading_aloud` : `${ns}.read_the_steps_aloud`,
      )}
      onPress={onToggleVoice}
      hitSlop={8}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 999,
        backgroundColor: voiceOn
          ? semanticColors.accent.tint
          : withAlpha(colors.softBrownMid, 0.08),
        opacity: pressed ? 0.7 : 1,
      })}
    >
      {voiceOn ? (
        <Volume2 size={14} color={EMBER.colors.text} />
      ) : (
        <VolumeX size={14} color={EMBER.colors.textLight} />
      )}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11.5,
          fontWeight: "600",
          color: voiceOn ? EMBER.colors.text : EMBER.colors.textLight,
        }}
      >
        {t(voiceOn ? `${ns}.stop_reading_aloud` : `${ns}.read_the_steps_aloud`)}
      </Text>
    </Pressable>
  );
}

/**
 * Pointing the phone, before the injection begins.
 *
 * **One action, deliberately.** A review asked for "Something's wrong" here,
 * because every other card in the walk reaches a human. This one does not, and
 * that is the decision: aiming a phone is not a medical step. Nothing has been
 * uncapped, nothing has been injected, and there is no clinical event to
 * escalate — the escalation card exists for the steps where there is. Adding it
 * would put a route to her clinic on the one screen where the answer is always
 * "move the phone", and the step's own warnings return with the step, one tap
 * away.
 */
function FramingStep({
  t,
  framing,
  shape,
  voiceOn,
  onToggleVoice,
  onReady,
}: {
  t: ReturnType<typeof useTranslations>;
  framing: Framing;
  voiceOn: boolean;
  onToggleVoice: () => void;
  onReady: () => void;
  /**
   * Which part she is being asked to frame.
   *
   * A seated thigh is not a body: her shoulders are usually out of shot, and
   * asking her to fit them in would be asking her to satisfy a check that has
   * nothing to do with where the needle goes.
   */
  shape: "torso" | "thigh";
}) {
  // "Ready" and "there is nothing to wait for" are different sentences and the
  // same button. A patient on a phone, or on a browser where the model would
  // not load, must not be left looking at a secondary action forever.
  const settled = framing === "ready" || framing === "unavailable";

  return (
    <Card>
      <View style={{ alignItems: "flex-end", marginBottom: -8 }}>
        <VoiceToggle t={t} voiceOn={voiceOn} onToggleVoice={onToggleVoice} />
      </View>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(
          shape === "thigh"
            ? `${ns}.sit_down_and_set_your_phone_so_it_sees_your_leg`
            : `${ns}.set_your_phone_so_it_can_see_you`,
        )}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, lineHeight: 21, color: EMBER.colors.textMid }}
      >
        {t(
          shape === "thigh"
            ? `${ns}.your_hip_and_your_knee_both_in_view`
            : `${ns}.stand_back_until_your_shoulders_and_hips_are_in_view`,
        )}
      </Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 14,
          backgroundColor:
            framing === "ready"
              ? semanticColors.status.successTint
              : withAlpha(colors.softBrownMid, 0.08),
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13.5,
            fontWeight: "700",
            color:
              framing === "ready"
                ? semanticColors.status.success
                : EMBER.colors.textMid,
          }}
        >
          {t(`${ns}.${FRAMING_MESSAGE[framing]}`)}
        </Text>
      </View>
      <CompactActionButton
        testID="injection-coach-framing-ready"
        variant={settled ? "primary" : "secondary"}
        label={t(`${ns}.i_am_ready_to_inject`)}
        onPress={onReady}
      />
    </Card>
  );
}

/**
 * What is wrong, in her words rather than the protocol's order.
 *
 * Every warning the protocol carries is offered, this step's first — because
 * `findWarning` in the reducer already searches the whole protocol, and the view
 * was the only thing narrowing it to `step.warnings[0]`. Ovidrel's single
 * `emergency` warning sits third on the aftercare step; before this it could
 * never be raised, and a patient short of breath after injecting was told not to
 * split her dose.
 *
 * "None of these" reaches a person without naming a reason. Not everything wrong
 * is on a list, and being unable to say which box you are in is not a reason to
 * be left alone with it.
 */
function WarningChooser({
  t,
  session,
  copy: copyContext,
  contacts,
  onCallClinic,
  onCallRecorded,
  onBack,
  onAbort,
}: {
  t: ReturnType<typeof useTranslations>;
  session: SessionState;
  copy: ProtocolCopyContext;
  /** Resolved at the top of the walk, so this screen never waits for them. */
  contacts: CoachEscalationContacts;
  onCallClinic: () => void;
  onCallRecorded: () => void;
  onBack: () => void;
  onAbort: () => void;
}) {
  const step = currentStep(session);
  const here = step?.warnings ?? [];
  const elsewhere = session.protocol.steps
    .filter((candidate) => candidate.id !== step?.id)
    .flatMap((candidate) => candidate.warnings ?? [])
    .filter(
      (warning, index, all) =>
        all.findIndex((other) => other.id === warning.id) === index,
    );

  /** Everything that would halt the walk, wherever in the procedure it sits. */
  const situations = [...here, ...elsewhere].filter(
    (warning) => warning.severity === "stop",
  );

  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.what_is_happening`)}
      </Text>

      {/* The numbers, before the list and not behind it.

        This screen used to be a list of symptoms, and a number arrived only
        after she picked the right one. That is a click and a decision between a
        patient who cannot breathe and a dispatcher, at the moment she is least
        able to make either. Reporting what is happening is still here — it is
        what pauses the walk and what the session records — but it is no longer
        the toll for reaching a person (Anaïs, 2026-09-22). */}
      <EscalationContacts
        t={t}
        locale={copyContext.locale}
        contacts={contacts}
        // Read from the list below rather than hardcoded. This screen used to
        // be reached by picking a symptom, which is what told it whether an
        // emergency was in play; nothing picks now, so it asks the warnings.
        // Without this the layout said "clinic first" while the sentence three
        // lines down said an allergic reaction needs the emergency services.
        isEmergency={situations.some(
          (warning) => warning.escalation === "emergency",
        )}
        onCallClinic={onCallClinic}
        onCallRecorded={onCallRecorded}
      />

      {/* Written, not offered.

        These were buttons: she picked the sentence that matched her situation,
        the walk paused, and the paused screen showed her that same sentence
        again with a number underneath it. The number is already above and the
        sentence already says what to do — so the tap was a toll charged to a
        patient who has something wrong, in exchange for what she could already
        read (Anaïs, 2026-09-22).

        Only the ones that halt. A caution is guidance on its own step rather
        than a situation she would be reporting. */}
      {situations.map((warning) => (
        <View
          key={warning.id}
          style={{
            flexDirection: "row",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            borderRadius: 12,
            backgroundColor:
              warning.escalation === "emergency"
                ? semanticColors.status.dangerTint
                : semanticColors.status.warningTint,
          }}
        >
          <AlertTriangle
            size={15}
            color={
              warning.escalation === "emergency"
                ? semanticColors.status.danger
                : semanticColors.status.warning
            }
            style={{ marginTop: 2 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13.5,
              lineHeight: 20,
              color: EMBER.colors.text,
            }}
          >
            {renderProtocolCopy(warning.copy, copyContext)}
          </Text>
        </View>
      ))}

      {/* Both ways out. "Stop for now" lived on the paused screen, which was
        only reachable by picking a symptom — so removing that tap would have
        removed the way to end a walk that is going wrong. */}
      <View style={{ flexDirection: "row", gap: 10 }}>
        <CompactActionButton
          variant="secondary"
          label={t(`${ns}.back_to_the_step`)}
          onPress={onBack}
        />
        <CompactActionButton
          testID="injection-coach-abort"
          variant="secondary"
          label={t(`${ns}.stop_for_now`)}
          onPress={onAbort}
        />
      </View>
    </Card>
  );
}

function NotStarted({
  t,
  protocol,
  onStart,
}: {
  t: ReturnType<typeof useTranslations>;
  protocol: InjectionProtocol;
  onStart: () => void;
}) {
  return (
    <Card>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Syringe size={18} color={EMBER.colors.text} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
        >
          {t(`${ns}.walk_me_through_it`)}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.one_step_at_a_time`, {
          params: { count: String(protocol.steps.length) },
        })}
      </Text>
      {/* Said before she starts, not buried at the end. */}
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, color: EMBER.colors.textMid, lineHeight: 20 }}
      >
        {t(`${ns}.follows_your_clinic_instructions`)}
      </Text>
      <CompactActionButton
        testID="injection-coach-start"
        variant="secondary"
        label={t(`${ns}.start_when_ready`)}
        onPress={onStart}
      />
    </Card>
  );
}

function InProgress({
  t,
  session,
  step,
  copy: copyContext,
  onAdvance,
  onConfirmAndAdvance,
  onBack,
  onSomethingWrong,
  voiceOn,
  onToggleVoice,
  showFigure,
  alreadyPrepared,
  cameraOffer,
  placementHint,
  site,
}: {
  t: ReturnType<typeof useTranslations>;
  session: SessionState;
  /**
   * Resolved by the walk rather than read from the session, because a procedure
   * with more than one site rewrites its cleaning step for the one she chose.
   */
  step: ProtocolStep | null;
  copy: ProtocolCopyContext;
  onAdvance: () => void;
  onConfirmAndAdvance: () => void;
  onBack: () => void;
  onSomethingWrong: () => void;
  voiceOn: boolean;
  onToggleVoice: () => void;
  /** False only where the camera is already showing the same diagram. */
  showFigure: boolean;
  /**
   * Her way out of a walk that assumed this is the evening she mixes the vial.
   *
   * Null on every step that is not preparing it, and on every procedure with
   * nowhere to send her. The walk she lands on opens by saying what it assumed,
   * so the tap is answered rather than silently obeyed.
   */
  alreadyPrepared: (() => void) | null;
  /**
   * The way back to a camera she turned off, where this step would show one.
   *
   * Null the rest of the time — on a step with no picture there is nothing to
   * turn back on, and an offer that appears on every step is an offer to start
   * a camera while she is washing her hands.
   */
  cameraOffer: { labelKey: string; onTurnOn: () => void } | null;
  /**
   * Where to put the phone, said only while there is a picture to justify it.
   *
   * On the guide this belongs to the aiming screen; a mirror has no aiming
   * screen, so without this the one procedure where she has to reach behind
   * herself never said to prop the phone at all. It was tried over the picture
   * and covered the control that turns the camera off, which is the more
   * important of the two — this is advice, that is a way out.
   */
  placementHint: boolean;
  /** Her answer, where the procedure offered a choice of site. */
  site: ProtocolSite["id"] | null;
}) {
  if (!step) return null;

  // The step's name and its instruction, shown as two blocks rather than one
  // run of bold text. The words and their order are unchanged — the copy always
  // carried the division, as a dash nobody was reading. See `stepCopy.ts`.
  const { title, body } = splitStepCopy(
    renderProtocolCopy(step.copy, copyContext),
  );
  const isInjection = step.kind === "irreversible";
  // Every step advances in one tap, the injection included. The tap carries the
  // confirmation when the step needs one, so the reducer still sees `confirm`
  // before `advance` and still refuses to pass the injection without it. See
  // `stepActions.ts` for what that does and does not preserve.
  /**
   * The drawing, or none.
   *
   * `figureForStep` keys off the step, and the cleaning step's drawing is a
   * lower abdomen with a navel. She may have chosen a thigh — in which case the
   * honest answer is no picture rather than a picture of somewhere else. A
   * thigh of her own is one she can see.
   */
  const figure =
    site && site !== "abdomen" && stepName(step.id) === "clean-site"
      ? null
      : figureForStep(step);

  const advanceAlsoConfirms = actionCarriesConfirmation(
    step,
    session.confirmedCurrentStep,
  );

  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, fontWeight: "700", color: EMBER.colors.textMid }}
      >
        {t(`${ns}.step_position`, {
          params: {
            current: String(session.stepIndex + 1),
            total: String(session.protocol.steps.length),
          },
        })}
      </Text>

      {/* On the counter line rather than among the actions: it is available on
        every step without ever sitting next to the button that advances past an
        injection. */}
      <View style={{ position: "absolute", top: 18, right: 18 }}>
        <VoiceToggle t={t} voiceOn={voiceOn} onToggleVoice={onToggleVoice} />
      </View>

      {/* Wrapped so the name and its instruction sit closer to each other than
        the card's own spacing would put them. They are one thought; a negative
        margin to fake that would be undone by the next person to touch the
        card's gap. */}
      <View style={{ gap: 6 }}>
        {title ? (
          <Text
            className="font-dm-sans"
            style={{
              // The injection reads heavier than the steps around it. She
              // should be able to tell she is at the moment that cannot be
              // undone without reading the words first.
              fontSize: isInjection ? 20 : 18,
              fontWeight: "800",
              color: EMBER.colors.text,
              lineHeight: isInjection ? 27 : 25,
            }}
          >
            {title}
          </Text>
        ) : null}

        <Text
          className="font-dm-sans"
          style={{
            // Lighter than the name above it, and looser. This is the sentence
            // she reads with her hands busy, and the card used to set the whole
            // thing in one bold block.
            fontSize: isInjection ? 16.5 : 15.5,
            fontWeight: title ? "500" : "800",
            color: title ? EMBER.colors.textMid : EMBER.colors.text,
            lineHeight: isInjection ? 25 : 23,
          }}
        >
          {body}
        </Text>

        {placementHint ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 19,
              color: EMBER.colors.textLight,
            }}
          >
            {t(`${ns}.set_your_phone_so_it_can_see_you`)}
          </Text>
        ) : null}
      </View>

      {showFigure && figure ? (
        <View style={{ alignItems: "center" }}>
          <StepOverlay figure={figure} tone="on-card" />
        </View>
      ) : null}

      {/* Said alongside the step, not offered as something to report. A caution
        never pauses the session, so the chooser cannot carry it — this is the
        only place it can be read. */}
      {(step.warnings ?? [])
        .filter((warning) => warning.severity === "caution")
        .map((warning) => (
          <ActionBanner
            key={warning.id}
            variant="warning"
            message={renderProtocolCopy(warning.copy, copyContext)}
          />
        ))}

      {/* Turning the camera off used to be final for the walk, which was true
        enough while the picture only ever appeared on the injection: the step
        it was for was the step she turned it off on. On a mirror it now
        appears one step earlier, so she can switch it off while swabbing and
        arrive at the injection — the step she cannot see — with no picture and
        no way back. This is the way back. It is also the only retry after a
        camera that failed to start. */}
      {cameraOffer ? (
        <CompactActionButton
          testID="injection-coach-turn-camera-on"
          variant="secondary"
          label={t(`${ns}.${cameraOffer.labelKey}`)}
          onPress={cameraOffer.onTurnOn}
        />
      ) : null}

      <View style={{ gap: 10 }}>
        <CompactActionButton
          testID="injection-coach-advance"
          variant="secondary"
          label={t(`${ns}.${advanceLabelKey(step)}`)}
          onPress={advanceAlsoConfirms ? onConfirmAndAdvance : onAdvance}
          disabled={!advanceAlsoConfirms && !canAdvance(session)}
        />

        {/* Under the step's own action, not beside "Something's wrong": this is
          not a problem she is reporting, it is the ordinary state of a vial
          kept for 28 days. Whoever is on the first evening never reads past
          the button above it. */}
        {alreadyPrepared ? (
          <CompactActionButton
            testID="injection-coach-vial-already-mixed"
            variant="secondary"
            label={t(`${ns}.my_vial_is_already_mixed`)}
            onPress={alreadyPrepared}
          />
        ) : null}

        <View style={{ flexDirection: "row", gap: 10 }}>
          {/* `canGoBack`, not `stepIndex > 0`: the reducer refuses to walk back
            over the injection, so on the aftercare step this button rendered
            enabled and did nothing. Same rule as the advance button above. */}
          {canGoBack(session) ? (
            <CompactActionButton
              variant="secondary"
              label={t(`${ns}.previous_step`)}
              onPress={onBack}
            />
          ) : null}
          {/* On every step, not only the ones that declare a warning. #2267
            requires any uncertain or distressing moment to reach a human, and
            four of Ovidrel's six steps used to offer no way out at all. */}
          <CompactActionButton
            testID="injection-coach-something-wrong"
            variant="secondary"
            label={t(`${ns}.something_is_wrong`)}
            onPress={onSomethingWrong}
          />
        </View>
      </View>
    </Card>
  );
}

/**
 * The numbers themselves, not a button that promises to find them.
 *
 * A paused session is the one place in this app where the next action is a
 * phone call, and until now it offered to navigate her to a contact screen —
 * which put us in the middle of an emergency call and, from a `Modal`, did not
 * even do that (the navigation happened invisibly underneath). So the digits are
 * on screen, she taps them, and the call is between her and whoever answers.
 *
 * Order is deliberate. Emergency services come first and only when the warning
 * asked for them, because an `emergency` escalation exists precisely to not read
 * like `clinic-now`. Her clinic follows, named where we know the name.
 *
 * The care-team button survives as the last resort, for a patient with no cycle
 * or no number on file. #2267 forbids a dead end here, and "no number" must
 * still lead somewhere.
 */
function EscalationContacts({
  t,
  locale,
  contacts,
  isEmergency,
  onCallClinic,
  onCallRecorded,
}: {
  t: ReturnType<typeof useTranslations>;
  locale: ProtocolCopyContext["locale"];
  /** Resolved at the top of the walk, not here. See the note on the hook call. */
  contacts: CoachEscalationContacts;
  isEmergency: boolean;
  onCallClinic: () => void;
  /** Recorded when she dials any of these, which is the act worth counting. */
  onCallRecorded: () => void;
}) {
  const { emergency, clinic } = contacts;
  const countryName = emergency
    ? (COUNTRIES.find((country) => country.code === emergency.countryCode) ??
      null)
    : null;

  const clinicBlock = clinic ? (
    <View
      style={{
        gap: 8,
        padding: 12,
        borderRadius: 14,
        backgroundColor: semanticColors.status.warningTint,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: EMBER.colors.textMid,
        }}
      >
        {t(`${ns}.contact_your_clinic`)}
      </Text>
      <PhoneLine
        testID="injection-coach-call-clinic"
        onCall={onCallRecorded}
        accessibilityLabel={t(`${ns}.call_number`, {
          params: { number: clinic.phone },
        })}
        number={clinic.phone}
        detail={clinic.name}
        tone={EMBER.colors.text}
      />
    </View>
  ) : (
    // No number on file. #2267 forbids a dead end here, so the care-team screen
    // is the last resort — and it is the only case left where reaching a person
    // costs her a navigation.
    <CompactActionButton
      testID="injection-coach-call-clinic"
      variant="secondary"
      label={t(`${ns}.call_my_clinic`)}
      onPress={onCallClinic}
      leading={<Phone size={16} color={EMBER.colors.text} />}
    />
  );

  /**
   * The emergency block, shown whether or not the warning asked for it.
   *
   * It used to appear only on an `emergency` escalation, which meant a patient
   * who tapped "Something's wrong" because she could not breathe had to first
   * find the right sentence in a list to be given a number. Nobody reads a list
   * in that state. The numbers are simply here — sized down and in the app's
   * own colours when nothing has asked for them, and framed in red when
   * something has (Anaïs, 2026-09-22).
   */
  const emergencyBlock = (
    <View
      style={{
        gap: 8,
        padding: 12,
        borderRadius: 14,
        backgroundColor: isEmergency
          ? semanticColors.status.dangerTint
          : withAlpha(colors.softBrownMid, 0.05),
        borderWidth: isEmergency ? 1 : 0,
        borderColor: withAlpha(semanticColors.status.danger, 0.22),
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: isEmergency
            ? semanticColors.status.danger
            : EMBER.colors.textMid,
        }}
      >
        {/* Named country, not a bare number. The region comes from the
              device's settings, so a patient who travels still sees her home
              country's services — reading the country name is how she catches
              that in the second where it matters. */}
        {emergency && countryName
          ? t(`${ns}.emergency_services_in_country`, {
              params: { country: getCountryName(countryName, locale) },
            })
          : t(`${ns}.call_your_local_emergency_services`)}
      </Text>
      {emergency?.numbers.map((entry) => (
        <PhoneLine
          key={entry.number}
          onCall={onCallRecorded}
          testID={`injection-coach-emergency-${entry.number}`}
          accessibilityLabel={t(`${ns}.call_number`, {
            params: { number: entry.number },
          })}
          number={entry.number}
          detail={t(
            entry.service === "medical"
              ? `${ns}.medical_dispatch`
              : `${ns}.all_emergency_services`,
          )}
          tone={isEmergency ? semanticColors.status.danger : EMBER.colors.text}
        />
      ))}
    </View>
  );

  // Emergency first wherever an emergency is among the situations listed, which
  // today is every procedure in the catalogue: all four medications carry a
  // warning about difficulty breathing. So in practice this screen always leads
  // with the services, and the clinic-first layout below is kept for a
  // procedure that carries no emergency at all. If that reads as too loud for a
  // spilled dose, the fix is to scope it to the situations on *this* step
  // rather than the whole procedure — a product call, not a code one.
  return (
    <View style={{ gap: 10 }}>
      {isEmergency ? emergencyBlock : clinicBlock}
      {isEmergency ? clinicBlock : emergencyBlock}
    </View>
  );
}

/** One dialable number, with who answers underneath it. */
function PhoneLine({
  number,
  detail,
  tone,
  testID,
  accessibilityLabel,
  onCall,
}: {
  number: string;
  /** Who answers, or the clinic's name. Omitted when we do not know. */
  detail: string | null;
  tone: string;
  testID: string;
  accessibilityLabel: string;
  /**
   * Recorded when she dials.
   *
   * The escalation used to be a button, and the button recorded it. Now that
   * the number itself is the action, the record follows the number — otherwise
   * every patient whose clinic has a phone on file reaches a human and the
   * session says she never did.
   */
  onCall: () => void;
}) {
  const href = telHref(number);

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      // A number we cannot turn into a `tel:` target still renders — she can
      // read it and dial it herself. It just is not pressable.
      disabled={href === null}
      onPress={() => {
        onCall();
        if (href) void Linking.openURL(href);
      }}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: pressed ? 0.62 : 1,
      })}
    >
      <Phone size={18} color={tone} />
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 20, fontWeight: "800", color: tone }}
        >
          {number}
        </Text>
        {detail ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12.5, color: EMBER.colors.textLight }}
          >
            {detail}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

/**
 * Which route her clinic prescribed, when the record does not say.
 *
 * Beside `DeviceQuestion` because it is the same kind of question and a
 * different axis: that one asks what she is holding, this asks where it goes.
 * Urinary hCG is one vial and two procedures — a long needle into the upper
 * outer quadrant of the buttock with aspiration, or a short one into the lower
 * abdomen — and nothing in a calendar entry that says "injection" distinguishes
 * them.
 *
 * Asked rather than guessed, and asked rather than skipped. A default would be
 * a site she may not have been told to use; silence would withdraw guidance
 * from the hardest injection in the catalogue. The answer is on the
 * prescription in her hand, and the copy says so.
 *
 * The two labels name the destination, not the abbreviation: "into the muscle"
 * and "under the skin" are what she was told, where "IM" and "SubQ" are what a
 * chart says.
 */
/**
 * Which site, on a procedure that has more than one.
 *
 * Both answers are in the approved content and neither is better than the
 * other, so this offers rather than recommends — and says the thing that makes
 * the choice easy, which is that it can be different tomorrow.
 */
function SiteQuestion({
  t,
  copy: copyContext,
  sites,
  onChoose,
  onSomethingWrong,
}: {
  t: ReturnType<typeof useTranslations>;
  copy: ProtocolCopyContext;
  sites: readonly ProtocolSite[];
  onChoose: (site: ProtocolSite["id"]) => void;
  onSomethingWrong: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.where_will_you_inject_it`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.you_can_change_sites_between_days`)}
      </Text>
      <View style={{ flexDirection: "row", gap: 10 }}>
        {sites.map((site) => (
          <CompactActionButton
            key={site.id}
            testID={`injection-coach-site-${site.id}`}
            variant="secondary"
            label={renderProtocolCopy(site.label, copyContext)}
            onPress={() => onChoose(site.id)}
          />
        ))}
      </View>

      {/* The same way out every other card has. This one sits on a step rather
        than beside one, so without it a patient who needs a person would have
        to answer a question about her body first — or close the coach. */}
      <CompactActionButton
        testID="injection-coach-something-wrong"
        variant="secondary"
        label={t(`${ns}.something_is_wrong`)}
        onPress={onSomethingWrong}
      />
    </Card>
  );
}

/**
 * Which leg, once she has said the thigh.
 *
 * Two buttons and no recommendation, because there is nothing to recommend:
 * both thighs are the same site and the reason to prefer one today is a bruise
 * on the other, which she can see and the app cannot. It says she can alternate
 * for the same reason the site question does — that is the thing that makes the
 * choice easy rather than a decision.
 *
 * Her own left and right, not the screen's. The preview is mirrored and the
 * silhouette is flipped to suit; see `kneePointsTo`.
 */
function LegQuestion({
  t,
  onChoose,
  onSomethingWrong,
}: {
  t: ReturnType<typeof useTranslations>;
  onChoose: (leg: ThighSide) => void;
  onSomethingWrong: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.which_thigh`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.alternate_legs_so_one_is_not_always_sore`)}
      </Text>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <CompactActionButton
          testID="injection-coach-leg-left"
          variant="secondary"
          label={t(`${ns}.my_left_thigh`)}
          onPress={() => onChoose("left")}
        />
        <CompactActionButton
          testID="injection-coach-leg-right"
          variant="secondary"
          label={t(`${ns}.my_right_thigh`)}
          onPress={() => onChoose("right")}
        />
      </View>

      <CompactActionButton
        testID="injection-coach-something-wrong"
        variant="secondary"
        label={t(`${ns}.something_is_wrong`)}
        onPress={onSomethingWrong}
      />
    </Card>
  );
}

export function RouteQuestion({
  t,
  onConfirmRoute,
}: {
  t: ReturnType<typeof useTranslations>;
  onConfirmRoute: (route: "intramuscular" | "subcutaneous") => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.how_did_your_clinic_tell_you_to_inject_it`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.the_site_and_the_needle_differ_by_route`)}
      </Text>
      {/* Rendered from the table rather than written out twice: a label and the
        route it selects come from the same row, so they cannot be swapped
        independently. See `routeChoices.ts`. */}
      <View style={{ flexDirection: "row", gap: 10 }}>
        {ROUTE_CHOICES.map((choice) => (
          <CompactActionButton
            key={choice.route}
            testID={choice.testID}
            variant="secondary"
            label={t(`${ns}.${choice.labelKey}`)}
            onPress={() => onConfirmRoute(choice.route)}
          />
        ))}
      </View>
    </Card>
  );
}

/**
 * What her calendar already says, offered rather than assumed.
 *
 * She answered "méthode d'administration" when she added the medication, and
 * the coach used to ask her again at the moment she is holding a syringe. This
 * shows her the answer instead, before anything starts, with one tap to agree
 * and one to correct it.
 *
 * ### Why it is shown at all rather than simply used
 *
 * A recorded device is a good guess and never an answer. Her pharmacy can
 * substitute one follitropin alfa device for another between cycles — in
 * France, at the counter, since ANSM's opinion of December 2024 — and the three
 * devices work so differently that the regulator's own committee noted dose
 * errors are already happening. The record may also predate a change, or have
 * arrived from an import. One tap is cheap; a walk for the wrong device is not.
 *
 * ### Why the correction writes back
 *
 * If she says the record is wrong, the record is wrong for every surface that
 * reads it, not just for this walk. Leaving the calendar saying one thing while
 * she injects another would make the next question worse, not better.
 */
export function DeviceConfirmation({
  t,
  label,
  fromHerRecord,
  onlyPresentation,
  onConfirm,
  onChange,
}: {
  t: ReturnType<typeof useTranslations>;
  /** The device being proposed, in her own language. */
  label: string;
  /**
   * Whether the proposal comes from her calendar or from the medication.
   *
   * Her record where she has one; otherwise the single presentation this
   * medication is sold as. Saying "your calendar says" about the second would
   * be the app quoting her on something she never told it.
   */
  fromHerRecord: boolean;
  /**
   * Whether this medication comes only one way.
   *
   * "The steps are different for each one" is true of Gonal-F and false of
   * Cetrotide, and the card shows on both now. What is worth a look on a
   * single-presentation medication is whether she has that presentation at all.
   */
  onlyPresentation: boolean;
  onConfirm: () => void;
  onChange: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(
          fromHerRecord
            ? `${ns}.your_calendar_says_you_use_this`
            : `${ns}.this_medication_comes_as_this`,
          { params: { device: label } },
        )}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(
          onlyPresentation
            ? `${ns}.the_steps_are_written_for_this_one`
            : `${ns}.the_steps_depend_on_it_so_it_is_worth_a_look`,
        )}
      </Text>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <CompactActionButton
          testID="injection-coach-device-confirm"
          variant="primary"
          label={t(`${ns}.yes_that_is_what_i_have`)}
          onPress={onConfirm}
        />
        <CompactActionButton
          testID="injection-coach-device-change"
          variant="secondary"
          label={t(`${ns}.no_i_have_something_else`)}
          onPress={onChange}
        />
      </View>
    </Card>
  );
}

/**
 * What the walk assumed about her vial, said before she acts on it.
 *
 * It was derived from the doses logged against this calendar line, and a
 * derived answer is a presumption, never a verdict — the rule already written
 * for the device her calendar records. One vial finished and the next one
 * opened looks exactly like a later evening from the outside, and only she can
 * see which it is.
 */
export function VialAlreadyOpenCard({
  t,
  onKeepUsing,
  onStartingNew,
}: {
  t: ReturnType<typeof useTranslations>;
  onKeepUsing: () => void;
  onStartingNew: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.your_vial_should_already_be_mixed`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.you_gave_a_dose_from_this_vial_recently`)}
      </Text>
      <CompactActionButton
        testID="injection-coach-vial-keep"
        variant="secondary"
        label={t(`${ns}.yes_i_am_using_that_vial`)}
        onPress={onKeepUsing}
      />
      <CompactActionButton
        testID="injection-coach-vial-new"
        variant="secondary"
        label={t(`${ns}.no_i_am_opening_a_new_one`)}
        onPress={onStartingNew}
      />
    </Card>
  );
}

/**
 * Which vial she has, when one medication ships as two that behave differently.
 *
 * Menopur's 75 and 150 IU vials are mixed fresh for every injection; its 600
 * and 1200 IU vials are mixed once and drawn from for up to 28 days. Both
 * arrive under the same "powder + solvent" button, and the two procedures
 * contradict each other at the end — one throws away what is left in the vial,
 * the other calls that the rest of her cycle.
 *
 * She is asked to read the number printed on the glass, never to know which
 * presentation she was prescribed. It is in her hand at this moment; the
 * prescription is not.
 *
 * Asked once, right after the device is confirmed (Anaïs, 2026-09-28). It began
 * as a step in the middle of the walk that told her to "stop here" and then
 * offered "next step", which is a promise the walk had no way to keep.
 */
export function PresentationQuestion({
  t,
  options,
  unit,
  onChoose,
  onNoneOfThese,
}: {
  t: ReturnType<typeof useTranslations>;
  /**
   * The container sizes this medication is actually sold as, where she is.
   *
   * Numbers from the procedures themselves, rendered with their own unit. They
   * used to be two translation keys holding Menopur's strengths — which the
   * next medication to declare a size would have inherited, with no type error
   * and no failing test, because the keys were bound to a medication-agnostic
   * pair of words.
   */
  options: readonly number[];
  /** The unit those sizes are printed in. Null only when there are none. */
  unit: string | null;
  onChoose: (size: number) => void;
  /**
   * Her vial says something that is not on the list.
   *
   * Without it the card is four numbers and an X. The device question has had
   * this escape since the start, for the same reason: a question she cannot
   * answer truthfully is one she answers wrongly.
   */
  onNoneOfThese: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.what_does_your_vial_say`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.the_steps_differ_by_vial_strength`)}
      </Text>
      <View style={{ flexDirection: "column", gap: 10 }}>
        {options.map((option) => (
          <CompactActionButton
            key={option}
            testID={`injection-coach-presentation-${option}`}
            variant="secondary"
            // The number and its unit, not a phrase: it is what is printed on
            // the glass, and matching it is the whole act she is asked to
            // perform.
            label={`${option}${unit ? ` ${unit}` : ""}`}
            onPress={() => onChoose(option)}
          />
        ))}
        <CompactActionButton
          testID="injection-coach-presentation-other"
          variant="secondary"
          label={t(`${ns}.my_vial_says_something_else`)}
          onPress={onNoneOfThese}
        />
      </View>
    </Card>
  );
}

export function DeviceQuestion({
  t,
  methods,
  unrecorded = false,
  mismatched = false,
  saved = false,
  onConfirmForm,
  onNoneOfThese,
}: {
  t: ReturnType<typeof useTranslations>;
  /**
   * The administration methods these procedures are actually dispensed as.
   *
   * In her calendar's vocabulary, not the coach's: she picked one of these when
   * she added the medication, and her answer here is the same value. Passed
   * rather than assumed — the list was a fixed pen-or-prefilled-syringe pair
   * once, and a patient was offered a syringe she does not own. See
   * `methodsFor`.
   */
  methods: readonly string[];
  /**
   * Whether she is being asked because her calendar has no answer.
   *
   * Said out loud when true. A question that arrives with no explanation reads
   * as an app that forgot; a question that says "we don't have this on file"
   * reads as one that checked. It also tells her, without a lecture, that
   * filling the field in would save her this next time.
   */
  unrecorded?: boolean;
  /**
   * Whether she answered and the answer is not one of these.
   *
   * "Flacon + seringue" is a vial of ready-made solution and every vial
   * procedure here is a powder to reconstitute. Her record holds a real answer
   * that this medication has no walk for, and saying "your calendar doesn't
   * say" would be the app blaming her for a gap of its own.
   */
  mismatched?: boolean;
  /**
   * Whether an answer here will actually be kept.
   *
   * The card promised "your answer will be saved for next time" to everyone,
   * including a patient reading the factsheet with no prescription behind it,
   * where the write is a no-op. A promise the app cannot keep is worse than no
   * promise.
   */
  saved?: boolean;
  onConfirmForm: (method: string) => void;
  /**
   * She has none of these.
   *
   * The pairing question has had this from the start and the device question
   * did not, which left a patient holding a presentation the catalogue does not
   * cover with nothing to press but a wrong answer. A wrong answer here starts
   * a walk for someone else's syringe, so the way out has to be on the card.
   */
  onNoneOfThese?: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.which_device_do_you_have`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(
          mismatched
            ? `${ns}.your_calendar_names_one_we_have_no_steps_for`
            : unrecorded
              ? saved
                ? `${ns}.your_calendar_does_not_record_which_one`
                : `${ns}.your_calendar_does_not_record_which_one_unsaved`
              : `${ns}.steps_differ_by_device`,
        )}
      </Text>
      <View style={{ flexDirection: "row", gap: 10 }}>
        {methods.map((method) => (
          <CompactActionButton
            key={method}
            testID={`injection-coach-device-${method}`}
            variant="secondary"
            // Her own form's wording, so the button she presses here and the
            // button she pressed when she added the medication say the same
            // thing about the same object.
            label={t(administrationMethodLabelKey(method))}
            onPress={() => onConfirmForm(method)}
          />
        ))}
      </View>
      {onNoneOfThese ? (
        <CompactActionButton
          testID="injection-coach-device-none"
          variant="secondary"
          label={t(`${ns}.i_have_none_of_these`)}
          onPress={onNoneOfThese}
        />
      ) : null}
    </Card>
  );
}

/**
 * Which box, where her device and her route still leave two walks.
 *
 * Beside `DeviceQuestion` because it is the same question one level down:
 * Mounjaro is a four-dose KwikPen or a single-dose pen, Wegovy a FlexTouch or
 * a single-dose pen, and both are "a pen" to the device question. The buttons
 * are the procedures' own labels — the box as she would recognise it — so a
 * new box is a new protocol, not a new table here.
 */
export function BoxQuestion({
  t,
  locale,
  boxes,
  onChoose,
  onNoneOfThese,
}: {
  t: ReturnType<typeof useTranslations>;
  locale: "en" | "fr";
  /** The boxes her remaining candidates come in, in catalogue order. */
  boxes: readonly ProtocolBox[];
  onChoose: (id: string) => void;
  /**
   * Her box is none of these. The device and vial questions have the same way
   * out, for the same reason: a wrong button starts someone else's walk.
   */
  onNoneOfThese: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.which_box_did_you_get`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.same_device_different_steps_by_box`)}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        {boxes.map((box) => (
          <CompactActionButton
            key={box.id}
            testID={`injection-coach-box-${box.id}`}
            variant="secondary"
            label={box.label[locale]}
            onPress={() => onChoose(box.id)}
          />
        ))}
      </View>
      <CompactActionButton
        testID="injection-coach-box-none"
        variant="secondary"
        label={t(`${ns}.i_have_none_of_these`)}
        onPress={onNoneOfThese}
      />
    </Card>
  );
}

/**
 * Her record names a dose the label does not, and the box in her hand is the
 * only thing that can settle it.
 *
 * The sentence quotes her record as she wrote it and names the label's unit;
 * the buttons are the label's own strengths, so her answer is one of the
 * label's numbers and nothing that passed through a text field. A product with
 * no fixed strengths has nothing to offer, so the one way on is a walk that
 * names no dose. See `doseQuestion.ts`.
 */
export function DoseQuestion({
  t,
  medicationName,
  question,
  onConfirmDose,
  onContinueWithoutDose,
  onUnlisted,
}: {
  t: ReturnType<typeof useTranslations>;
  medicationName: string;
  question: DoseQuestionState;
  onConfirmDose: (dose: string) => void;
  onContinueWithoutDose: () => void;
  onUnlisted: () => void;
}) {
  const mismatch = question.mismatch;
  if (!mismatch) return null;
  const explanation =
    mismatch.reason === "unit"
      ? t(`${ns}.your_record_says_a_dose_in_another_unit`, {
          params: {
            recorded: mismatch.recorded.text,
            medication: medicationName,
            unit: mismatch.labelUnit,
          },
        })
      : t(`${ns}.your_record_says_a_dose_this_product_does_not_come_in`, {
          params: {
            recorded: mismatch.recorded.text,
            medication: medicationName,
          },
        });

  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.what_does_your_box_say`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {explanation}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {question.options.length > 0
          ? t(`${ns}.check_the_box_and_correct_your_record`)
          : t(`${ns}.the_steps_will_not_name_a_dose`)}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        {question.options.map((option) => (
          <CompactActionButton
            key={option}
            testID={`injection-coach-dose-${option.replaceAll(/\s+/gu, "-")}`}
            variant="secondary"
            label={option}
            onPress={() => onConfirmDose(option)}
          />
        ))}
        {question.options.length > 0 ? (
          <CompactActionButton
            testID="injection-coach-dose-unlisted"
            variant="secondary"
            label={t(`${ns}.it_says_something_else`)}
            onPress={onUnlisted}
          />
        ) : (
          <CompactActionButton
            testID="injection-coach-dose-without"
            variant="secondary"
            label={t(`${ns}.continue_without_naming_the_dose`)}
            onPress={onContinueWithoutDose}
          />
        )}
      </View>
    </Card>
  );
}

/**
 * Her box matches none of the strengths the label lists.
 *
 * The honest end: nothing here can place that dose, so there is no walk, and
 * the card says why and where to go — her clinic for the prescription, her
 * record for the number. The only control is the sheet's own close.
 */
export function DoseNotCovered({
  t,
  question,
}: {
  t: ReturnType<typeof useTranslations>;
  question: DoseQuestionState;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.we_cannot_guide_this_dose`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.the_steps_are_written_for_these_doses`, {
          params: { doses: question.options.join(" / ") },
        })}
      </Text>
    </Card>
  );
}
